import { styles } from '../view/styles';
import { FormFieldInput } from '../../../../../../../../ui/gzForm/formField/component/FormFieldInput';
import { PoliciesApiClientsType } from '../../../../dependencies/PolicyDependenciesContainer';
import { query } from 'lit/decorators.js';
import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../form';
import { html } from 'lit';
import { TemplateResult } from 'lit/development';
import { OnNewGridItemAdd } from '../../../events/events';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { GridCellEditWebComponent } from '../../../../../../../../grid/uiElements/gridCellEdit';

export const GRID_ITEM_FORM_ID = 'policy-ui-settings-grid-item-form';

export abstract class GzPolicyUiSettingsBaseGrid<Column, GridItem, GridItemFormValue> extends FormFieldInput<
  GridItem[]
> {
  // column details to be set by the child class
  protected abstract columns: Column[];

  // grid inline row actions
  protected hasDeleteRowAction = true;
  protected hasEditRowAction = true;

  // dependencies
  protected clients: PoliciesApiClientsType;

  // policy id
  protected policyId;

  // grid items
  protected initialGridItems: GridItem[] = [];

  // searchable keywords
  protected searchableKeywords;

  // selectors
  @query('#' + GRID_ITEM_FORM_ID)
  protected gridItemForm: GzPolicyUiSettingsGridItemFormWebComponent;

  /**
   * Styles for the component.
   */
  static get styles() {
    return styles;
  }

  /* Form values region */
  /**
   * Get form field value as an array of objects with id and name fields.
   */
  public abstract getFormValue(): GridItem[];

  /**
   * Set form field value.
   * @param value
   */
  public setFormValue(value: GridItem[]) {
    this.initialGridItems = value;
    this.computeGridValues();
  }

  /**
   * Set policy id.
   * @param policyId
   */
  public setPolicyId(policyId: string) {
    this.policyId = policyId;
  }

  /**
   * Resets component properties.
   */
  public reset() {
    this.gridItemForm?.reset();
    this.initialGridItems = [];
    this.computeGridValues();
  }

  /**
   * Set grid values.
   */
  protected abstract computeGridValues();

  /**
   * Overrides FormFieldInput method for comparing two arrays of objects
   * (this component uses array as form field value)
   * False is returned, because comparing two grids values is time-consuming.
   * @param value1
   * @param value2
   */
  public areFormValuesEqual(value1: GridItem[], value2: GridItem[]) {
    return false;
  }
  /* Form values end region */

  /* Render region */
  /**
   * Render specific content.
   */
  protected render() {
    return html`
      <div class="header-buttons-outside-grid" part="header-buttons">${this.renderHeaderButtonsOutsideGrid()}</div>
      <div class="grid-items" @onNewGridItemAdd="${e => this.onNewGridItemAdd(e.detail)}">
        ${this.renderGridItemForm()}
      </div>
      ${this.renderGrid()}
    `;
  }

  /**
   * Render custom grid component.
   */
  protected abstract renderGrid();

  /**
   * Render header buttons. Override this method to add custom header buttons.
   * This method is useful when you have grid item form, because buttons should be rendered outside the grid.
   */
  protected renderHeaderButtonsOutsideGrid(): TemplateResult | undefined {
    return;
  }

  /**
   * Render grid item form. Override this method to add custom grid item form.
   */
  protected renderGridItemForm(): TemplateResult | undefined {
    return;
  }
  /* Render end region */

  /* Delete region */
  /**
   * Remove grid item by index.
   * @param event
   */
  protected onDeleteGridItem(event: CustomEvent) {}
  /* Delete end region */

  /* Add region */
  /**
   * Event handler for new grid item add.
   * @param item
   * @param position - if position is provided, item will be added at the specified position
   */
  protected onNewGridItemAdd(item: GridItemFormValue, position?: number): ValidationResult {
    const sanitizedItem = this.sanitizeGridItemBeforeAdd(item);
    const validationResponse = this.validateGridItemBeforeAdd(sanitizedItem);

    // if invalid, set error and return
    if (!validationResponse.valid) {
      this.gridItemForm?.setError(validationResponse.error || '');
      return validationResponse;
    }

    // add new item to the grid
    this.addGridItem(this.convertGridItemBeforeAdd(sanitizedItem), position);
    this.onGridItemsChange();

    // reset form
    this.gridItemForm?.resetOnAdd();
    return validationResponse;
  }

  /**
   * Add grid item to the grid. Implement this method to add custom logic.
   * @param gridItem
   * @param position - if position is provided, item will be added at the specified position
   * @protected
   */
  protected abstract addGridItem(gridItem: GridItem, position?: number): void;

  /**
   * Sanitize grid item before adding to the grid. Override this method to add custom sanitization.
   * @param gridItemFormValue
   */
  protected sanitizeGridItemBeforeAdd(gridItemFormValue: GridItemFormValue): GridItemFormValue {
    return gridItemFormValue;
  }

  /**
   * Validate grid item before adding to the grid. Override this method to add custom validation.
   * @param gridItemFormValue
   */
  protected validateGridItemBeforeAdd(gridItemFormValue: GridItemFormValue): ValidationResult {
    return { valid: true };
  }

  /**
   * Convert grid item before adding to the grid. Override this method to add custom conversion.
   * @param gridItemFormValue
   */
  protected convertGridItemBeforeAdd(gridItemFormValue: GridItemFormValue): GridItem {
    return gridItemFormValue as unknown as GridItem;
  }
  /* Add end region */

  /* Edit region */
  /**
   * Event handler for edit row enabled. Override this method to add custom logic (e.g. validation).
   * @param event
   */
  protected onEditRowEnabled(event: CustomEvent): void {}

  /**
   * Event handler for edit row cancel. Override this method to add custom logic.
   * @param event
   */
  protected onEditRowCancel(event: CustomEvent): void {}

  /**
   * Event handler for edit row save.
   * @param event
   */
  protected onEditRowSave(event: CustomEvent): void {}

  /**
   * Check if edited row is changed. Override this method to add custom logic.
   * @param oldItem
   * @param newItem
   */
  protected isEditedRowChanged(oldItem: GridItem, newItem: GridItem): boolean {
    return true;
  }

  /**
   * Validate grid item before editing. Override this method to add custom validation.
   * @param oldItem
   * @param newItem
   */
  protected validateGridItemBeforeEdit(oldItem: GridItem, newItem: GridItem): ValidationResult {
    return { valid: true };
  }

  /**
   * Sanitize edited grid item before adding to the grid. Override this method to add custom conversion.
   * @param gridItem
   */
  protected sanitizeGridItemBeforeEdit(gridItem: GridItem): GridItem {
    return gridItem;
  }

  /**
   * Revert grid cell edit value. Override this method to add custom logic.
   * @param gridCellEdit
   * @param oldItem
   */
  protected revertGridCellEditValue(gridCellEdit: GridCellEditWebComponent, oldItem: GridItem): void {
    return;
  }

  /**
   * Method used for handling grid items change. Override this method to add custom logic.
   */
  protected onGridItemsChange(): void {
    return;
  }
  /* Edit end region */
}
